import { createContext, ReactElement, useReducer } from "react"
import axios from "utils/axios"

import {
  FETCH_AGREEMENT_UPLOADS,
  FETCH_CONFIRMED_OFFERS,
  FETCH_REPORT_UPLOAD_REQUESTS,
  FETCH_CURRENCY_EXCHANGE_RATES,
  FETCH_DEAL_OFFER_STATISTICS,
  FETCH_DEAL_OFFER_BI,
  DELETE_DEAL_OFFER
} from "../store/reducers/actions"
import {
  mapListToCamelCase,
  mapPaginatedDataToCamelCase,
  mapToCamelCase,
  mapToSnakeCase
} from "../utils/caseTransformation"
import { PaginatedData } from "types/root"
import {
  ReportsContextType,
  ConfirmedOffer,
  ConfirmedOfferTypes,
  ConfirmedOffersFilterType,
  AgreementUploadFilterType,
  AgreementUpload,
  ReportUploadRequest,
  ReportUploadRequestFilterType,
  CurrencyExchangeRate,
  DealOfferStatistics,
  OfferVariationTemplate,
  RecoupVariationInput,
  RecoupResult,
  ArtistProfileBundle,
  DealOfferBi,
  DealOverviewCounts
} from "types/reports"
import reportsReducer, { initialReportsState } from "store/reducers/reports"
import { getFilterText, formatLocalDate, removeEmptyParams, toastMessage } from "utils"

const ReportsContext = createContext<ReportsContextType | null>(null)

export const ReportsProvider = ({ children }: { children: ReactElement }) => {
  const [state, dispatch] = useReducer(reportsReducer, initialReportsState)

  const fetchConfirmedOfferList = async (
    limit: number,
    offset: number,
    isPublic: boolean,
    filter?: ConfirmedOffersFilterType,
    orderBy?: string,
    id?: number,
    userId?: number,
    isDealOffers?: boolean
  ) => {
    const idParam = id ? `&id=${id}` : ""
    const userIdParam = userId ? `&user_id=${userId}` : ""
    const orderByParam = orderBy ? `&order_by=${orderBy}` : ""

    const tagIncludedLength = filter?.tagsIncluded?.length || 0
    const tagExcludedLength = filter?.tagsExcluded?.length || 0
    const tagsIncludedParam = tagIncludedLength > 0 ? `&tags_included=${filter?.tagsIncluded.join(",")}` : ""
    const tagsExcludedParam = tagExcludedLength > 0 ? `&tags_excluded=${filter?.tagsExcluded.join(",")}` : ""
    const dealStatusParam = filter?.dealStatus ? `&deal_status=${filter.dealStatus}` : ""
    const [dealDateStart, dealDateEnd] = filter?.dealDateRange || [null, null]
    const dealDateStartParam = dealDateStart ? `&deal_created_start=${formatLocalDate(dealDateStart)}` : ""
    const dealDateEndParam = dealDateEnd ? `&deal_created_end=${formatLocalDate(dealDateEnd)}` : ""
    const filteredFilter = {
      ...filter,
      tagsIncluded: undefined,
      tagsExcluded: undefined,
      dealStatus: undefined,
      dealDateRange: undefined
    }
    const isDealOffersParam = isDealOffers ? "&deal_offers=1" : ""

    const params = `?limit=${limit}&offset=${offset}&scouting=1${orderByParam}${idParam}${userIdParam}${getFilterText(
      filteredFilter
    )}${tagsIncludedParam}${tagsExcludedParam}${dealStatusParam}${dealDateStartParam}${dealDateEndParam}${isDealOffersParam}`

    const baseUrl = isPublic ? "/public/offers/confirmed_offers" : `/reports/confirmed-offers`
    const url = `${baseUrl}/${params}`
    const response = await axios(url, { method: "get" })

    dispatch({
      type: FETCH_CONFIRMED_OFFERS,
      payload: {
        ...state,
        paginatedConfirmedOfferList: mapPaginatedDataToCamelCase<PaginatedData<ConfirmedOffer>>(response.data)
      }
    })
  }

  const updateConfirmedOffer = async (
    id: number,
    approved: boolean,
    type: string,
    splitRate?: number,
    offer?: number
  ) => {
    const response = await axios(`/reports/confirmed-offers/save/`, {
      method: "put",
      data: {
        id,
        offer,
        approved,
        type,
        split_rate: splitRate
      }
    })

    if (!response.data.success) {
      return false
    }

    const results = [...state.paginatedConfirmedOfferList.results].map((confirmedOffer: ConfirmedOffer) => {
      if (confirmedOffer.id === id) {
        if (offer && confirmedOffer.type === ConfirmedOfferTypes.ADVANCE) {
          confirmedOffer.advanceOffer = offer
        } else if (offer && confirmedOffer.type === ConfirmedOfferTypes.BUYOUT) {
          confirmedOffer.buyoutOffer = offer
        }

        confirmedOffer.approved = approved
        confirmedOffer.type = type
        confirmedOffer.splitRate = splitRate
      }
      return confirmedOffer
    })

    dispatch({
      type: FETCH_CONFIRMED_OFFERS,
      payload: {
        ...state,
        paginatedConfirmedOfferList: { ...state.paginatedConfirmedOfferList, results }
      }
    })

    return true
  }

  const deleteDealOffer = async (id: number) => {
    const response = await axios(`/reports/confirmed-offers/delete_deal_offer/?id=${id}`, { method: "delete" })
    if (!response.data.success) {
      toastMessage(`Error deleting the deal`, "error")
      return false
    }

    const results = state.paginatedConfirmedOfferList.results.map((result) => ({
      ...result,
      dealOffers: result.dealOffers.filter((dealOffer) => dealOffer.id !== id)
    }))

    toastMessage(`Deal deleted succesfully.`, "success")

    dispatch({
      type: DELETE_DEAL_OFFER,
      payload: {
        ...state,
        paginatedConfirmedOfferList: { ...state.paginatedConfirmedOfferList, results: [...results] }
      }
    })
    return true
  }

  const updateSongSelectionOffer = async (confirmedOfferId: number, songSelectionId: number, offer: number) => {
    const response = await axios(`/reports/confirmed-offers/song_selection_offer/`, {
      method: "put",
      data: { confirmed_offer_id: confirmedOfferId, song_selection_id: songSelectionId, offer_usd: offer }
    })

    if (!response.data.success) {
      return false
    }

    return true
  }

  const bulkUpdateSongSelectionOffer = async (
    confirmedOfferId: number,
    updates: { songSelectionId: number; offer: number }[]
  ) => {
    const response = await axios(`/reports/confirmed-offers/bulk_song_selection_offer/`, {
      method: "put",
      data: {
        confirmed_offer_id: confirmedOfferId,
        updates: updates.map((u) => ({ song_selection_id: u.songSelectionId, offer_usd: u.offer }))
      }
    })

    if (!response.data.success) {
      return false
    }

    return true
  }

  const saveNewVersionConfirmedOffer = async (id: number, type: string, offer: number) => {
    const response = await axios(`/reports/confirmed-offers/save_new_version/`, {
      method: "put",
      data: {
        id,
        offer,
        type
      }
    })

    if (!response.data.success) {
      return false
    }

    // Update local state with new offer value
    const results = [...state.paginatedConfirmedOfferList.results].map((confirmedOffer: ConfirmedOffer) => {
      if (confirmedOffer.id === id) {
        if (type === ConfirmedOfferTypes.ADVANCE) {
          confirmedOffer.advanceOffer = offer
        } else if (type === ConfirmedOfferTypes.BUYOUT) {
          confirmedOffer.buyoutOffer = offer
        }
        confirmedOffer.type = type
      }
      return confirmedOffer
    })

    dispatch({
      type: FETCH_CONFIRMED_OFFERS,
      payload: {
        ...state,
        paginatedConfirmedOfferList: { ...state.paginatedConfirmedOfferList, results }
      }
    })

    return true
  }

  const fetchAgreementUploadList = async (
    limit: number,
    offset: number,
    filter?: AgreementUploadFilterType,
    orderBy?: string
  ) => {
    const url = `/reports/royalty/agreement_uploads/?limit=${limit}&offset=${offset}&artist_id=${filter?.artistId}`

    const response = await axios(url, { method: "get" })

    dispatch({
      type: FETCH_AGREEMENT_UPLOADS,
      payload: {
        ...state,
        paginatedAgreementUploadList: mapPaginatedDataToCamelCase<PaginatedData<AgreementUpload>>(response.data)
      }
    })
  }

  const uploadAgreementFile = async (formData: any, artistId: number) => {
    const url = `/reports/royalty/upload_agreement_file/?artist_id=${artistId}`

    const response = await axios.post(url, formData)

    if (!response.data.result) {
      return false
    }

    const agreementUpload: AgreementUpload = mapToCamelCase(response.data.result)
    const results = [agreementUpload, ...state.paginatedAgreementUploadList.results]

    dispatch({
      type: FETCH_AGREEMENT_UPLOADS,
      payload: {
        ...state,
        paginatedAgreementUploadList: { ...state.paginatedAgreementUploadList, results }
      }
    })

    return true
  }

  const mergeConfirmedOffers = async (confirmedOfferIds: number[]) => {
    const idParam = confirmedOfferIds.join(",")
    const response = await axios(`/reports/confirmed-offers/merge/?offer_ids=${idParam}`, { method: "post" })

    if (!response.data.success) {
      return false
    }

    return true
  }

  const fetchReportUploadRequestList = async (
    limit: number,
    offset: number,
    filter?: ReportUploadRequestFilterType,
    orderBy?: string
  ) => {
    const url = `/reports/report-upload-request/?limit=${limit}&offset=${offset}${getFilterText(filter)}`

    const response = await axios(url, { method: "get" })

    dispatch({
      type: FETCH_REPORT_UPLOAD_REQUESTS,
      payload: {
        ...state,
        paginatedReportUploadRequestList: mapPaginatedDataToCamelCase<PaginatedData<ReportUploadRequest>>(response.data)
      }
    })
  }

  const fetchCurrencyExchangeRates = async () => {
    const url = `/reports/currency-exchange/`
    const response = await axios.get(url)

    if (!response.data.results) {
      return
    }

    const currencyExchangeRates: CurrencyExchangeRate = response.data.results

    dispatch({
      type: FETCH_CURRENCY_EXCHANGE_RATES,
      payload: {
        ...state,
        currencyExchangeRates
      }
    })
  }

  const fetchDealOffersStatistics = async (filter?: ConfirmedOffersFilterType) => {
    const response = await axios.get("/reports/deal-offers/statistics/", {
      params: removeEmptyParams(mapToSnakeCase(filter))
    })

    dispatch({
      type: FETCH_DEAL_OFFER_STATISTICS,
      payload: {
        ...state,
        dealOfferStatistics: mapToCamelCase<DealOfferStatistics>(response.data)
      }
    })
  }

  // Offers v2 — variation templates / recoup calc / artist profile bundle.
  let cachedVariationTemplates: OfferVariationTemplate[] | null = null

  const getVariationTemplates = async (): Promise<OfferVariationTemplate[]> => {
    if (cachedVariationTemplates) return cachedVariationTemplates
    const response = await axios.get("/reports/deal-offers/variation_templates/")
    cachedVariationTemplates = mapListToCamelCase<OfferVariationTemplate>(response.data)
    return cachedVariationTemplates
  }

  const calculateRecoup = async (
    confirmedOfferId: number,
    variations: RecoupVariationInput[],
    predictionVersion?: number
  ): Promise<RecoupResult[]> => {
    const response = await axios.post("/reports/deal-offers/calculate_recoup/", {
      confirmed_offer_id: confirmedOfferId,
      prediction_version: predictionVersion ?? 2,
      variations: variations.map((v) => mapToSnakeCase(v))
    })
    return mapListToCamelCase<RecoupResult>(response.data?.results || [])
  }

  const getArtistProfile = async (confirmedOfferId: number): Promise<ArtistProfileBundle> => {
    const response = await axios.get("/reports/confirmed-offers/artist_profile/", {
      params: { confirmed_offer_id: confirmedOfferId }
    })
    return mapToCamelCase<ArtistProfileBundle>(response.data)
  }

  const fetchDealOffersBi = async (filter?: ConfirmedOffersFilterType) => {
    const response = await axios.get("/reports/deal-offers/bi/", {
      params: removeEmptyParams(mapToSnakeCase(filter))
    })

    dispatch({
      type: FETCH_DEAL_OFFER_BI,
      payload: {
        ...state,
        dealOfferBi: mapToCamelCase<DealOfferBi>(response.data)
      }
    })
  }

  // Counts for the Deals page overview cards. Mirrors the list filter (date range + other
  // filters) but intentionally omits the card selections (deal status, exchange rate) so every
  // card stays visible regardless of which one is currently active in the list.
  const fetchDealStatusCounts = async (
    filter?: ConfirmedOffersFilterType,
    isDealOffers?: boolean
  ): Promise<DealOverviewCounts> => {
    const tagIncludedLength = filter?.tagsIncluded?.length || 0
    const tagExcludedLength = filter?.tagsExcluded?.length || 0
    const tagsIncludedParam = tagIncludedLength > 0 ? `&tags_included=${filter?.tagsIncluded.join(",")}` : ""
    const tagsExcludedParam = tagExcludedLength > 0 ? `&tags_excluded=${filter?.tagsExcluded.join(",")}` : ""
    const [dealDateStart, dealDateEnd] = filter?.dealDateRange || [null, null]
    const dealDateStartParam = dealDateStart ? `&deal_created_start=${formatLocalDate(dealDateStart)}` : ""
    const dealDateEndParam = dealDateEnd ? `&deal_created_end=${formatLocalDate(dealDateEnd)}` : ""
    const filteredFilter = {
      ...filter,
      tagsIncluded: undefined,
      tagsExcluded: undefined,
      dealStatus: undefined,
      exchangeRateUnavailable: undefined,
      dealDateRange: undefined
    }
    const isDealOffersParam = isDealOffers ? "&deal_offers=1" : ""

    const params = `?scouting=1${getFilterText(
      filteredFilter
    )}${tagsIncludedParam}${tagsExcludedParam}${dealDateStartParam}${dealDateEndParam}${isDealOffersParam}`

    const response = await axios(`/reports/confirmed-offers/deal_status_counts/${params}`, { method: "get" })
    return {
      statusCounts: response.data?.status_counts || {},
      exchangeRateUnavailable: response.data?.exchange_rate_unavailable || { count: 0, amount: 0 }
    }
  }

  return (
    <ReportsContext.Provider
      value={{
        ...state,
        fetchConfirmedOfferList,
        fetchDealStatusCounts,
        updateConfirmedOffer,
        saveNewVersionConfirmedOffer,
        updateSongSelectionOffer,
        bulkUpdateSongSelectionOffer,
        fetchAgreementUploadList,
        uploadAgreementFile,
        fetchReportUploadRequestList,
        fetchCurrencyExchangeRates,
        mergeConfirmedOffers,
        fetchDealOffersStatistics,
        getVariationTemplates,
        calculateRecoup,
        getArtistProfile,
        fetchDealOffersBi,
        deleteDealOffer
      }}
    >
      {children}
    </ReportsContext.Provider>
  )
}

export default ReportsContext
